// Mantine
import { ActionIcon, NumberInput, Anchor, Modal, Tooltip } from '@mantine/core'

// Icons
import {
    IconPhotoOff,
    IconX,
    IconAlertTriangle,
    IconCurrencyEuroOff,
} from '@tabler/icons-react'

// React
import { useState } from 'react'

/**
 * Universal line item display component
 * Works with both components and shopify variants via normalized data from useLineItems hook
 */
export default function LineItemDisplay({
    line,
    onRemove,
    onPriceChange,
    onQuantityChange,
    hidePrice = false,
    hasAvailabilityIssue = false,
}) {
    // Price edit modal
    const [isPriceEditModalOpen, setIsPriceEditModalOpen] = useState(false)

    // Generate the correct link based on item type
    const getItemLink = () => {
        if (line.item_type === 'component') {
            return `/components/${line.component_id}`
        } else if (line.item_type === 'shopify_product') {
            return `/products/shopify/${line.product_id}`
        }
        return '#'
    }

    return (
        <>
            <div className="grid grid-cols-[60px_1fr_150px] items-center gap-5 py-2">
                <div className="aspect-square h-[60px] bg-gray-100 rounded-lg relative">
                    {line.image_url ? (
                        <img
                            src={line.image_url + '&width=100'}
                            className="w-full h-full p-2 object-contain"
                            alt={line.display_title}
                        />
                    ) : (
                        <div className="flex items-center justify-center w-full h-full text-gray-400">
                            <IconPhotoOff size="1.5rem" />
                        </div>
                    )}
                </div>

                <div className="flex flex-col items-start">
                    <div className="flex items-center gap-2">
                        <Anchor href={getItemLink()}>
                            {line.display_title}
                        </Anchor>
                        {hasAvailabilityIssue && (
                            <IconAlertTriangle
                                size={16}
                                className="text-orange-500"
                            />
                        )}
                    </div>

                    {line.display_subtitle && (
                        <span className="text-gray-500">
                            {line.display_subtitle}
                        </span>
                    )}

                    <span className="text-gray-400 text-xs">{line.sku}</span>

                    {!hidePrice && (
                        <div className="mt-1 flex items-center gap-1">
                            <Anchor
                                size="sm"
                                onClick={() => setIsPriceEditModalOpen(true)}
                            >
                                {new Intl.NumberFormat('fr-FR', {
                                    style: 'currency',
                                    currency: 'EUR',
                                }).format(line.unit_price)}
                            </Anchor>
                            {line.item_type === 'component' &&
                                !line._component?.value && (
                                    <Tooltip label="Valeur d'achat non définie">
                                        <IconCurrencyEuroOff
                                            size={14}
                                            className="text-orange-500"
                                        />
                                    </Tooltip>
                                )}
                        </div>
                    )}
                </div>

                <div className="flex gap-2 justify-end items-start">
                    <div className="flex flex-col gap-2 items-center">
                        <div className="flex items-center gap-2">
                            <NumberInput
                                clampBehavior="strict"
                                min={1}
                                w={100}
                                value={line.quantity}
                                prefix="×"
                                onChange={(value) => onQuantityChange(value)}
                            />
                        </div>
                    </div>

                    <ActionIcon
                        size="lg"
                        variant="subtle"
                        color="dark"
                        onClick={() => onRemove()}
                    >
                        <IconX size="1rem" />
                    </ActionIcon>
                </div>
            </div>

            {!hidePrice && (
                <Modal
                    opened={isPriceEditModalOpen}
                    onClose={() => setIsPriceEditModalOpen(false)}
                    title="Modifier le prix"
                >
                    <NumberInput
                        min={0.01}
                        decimalScale={2}
                        fixedDecimalScale
                        clampBehavior="strict"
                        suffix=" €"
                        value={line.unit_price}
                        decimalSeparator=","
                        thousandSeparator=" "
                        onChange={(value) => {
                            onPriceChange(value)
                            setIsPriceEditModalOpen(false)
                        }}
                    />
                </Modal>
            )}
        </>
    )
}
